import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ── Types ─────────────────────────────────────────────────────────────────────
/** Column widths keyed by column `field` string, value is width in pixels. */
type WidthMap = Record<string, number>;

interface ColumnResizeState {
  /** Persisted column widths for the TAL Accounts grid. */
  talAccounts: WidthMap;
  /** Persisted column widths for the TAL Contacts grid. */
  talContacts: WidthMap;
  /** Persisted column widths for the WAL (Website Account List) Accounts grid. */
  walAccounts: WidthMap;
  /** Persisted column widths for the WAL (Website Account List) Contacts grid. */
  walContacts: WidthMap;
  /** Persisted column widths for the List Details Contacts grid. */
  listContacts: WidthMap;
  /** Persisted column widths for the List Details Accounts grid. */
  listAccounts: WidthMap;
  /** Persisted column widths for the MDQS (Database Search) Accounts grid. */
  mdqsAccounts: WidthMap;
  /** Persisted column widths for the MDQS (Database Search) Contacts grid. */
  mdqsContacts: WidthMap;
  /** Persisted column widths for the Account Details page's Contacts tab table. */
  accountDetailsContacts: WidthMap;
}

// ── Initial state ─────────────────────────────────────────────────────────────
const initialState: ColumnResizeState = {
  talAccounts: {},
  talContacts: {},
  walAccounts: {},
  walContacts: {},
  listContacts: {},
  listAccounts: {},
  mdqsAccounts: {},
  mdqsContacts: {},
  accountDetailsContacts: {},
};

// ── Slice ─────────────────────────────────────────────────────────────────────
const columnResizeSlice = createSlice({
  name: 'columnResize',
  initialState,
  reducers: {
    /**
     * Persist a single column width for a specific page context.
     * Called (debounced) after the user finishes dragging a resize handle.
     */
    setColumnWidth: (
      state,
      action: PayloadAction<{
        context: keyof ColumnResizeState;
        field: string;
        width: number;
      }>,
    ) => {
      const { context, field, width } = action.payload;
      // Guard: if redux-persist rehydrated stale state that predates a key rename
      // the context bucket may be undefined.  Initialise it on first write.
      if (!state[context]) state[context] = {};
      state[context][field] = width;
    },

    /**
     * Bulk-replace all widths for a context.
     * Not currently used externally but useful for future resets / imports.
     */
    setAllColumnWidths: (
      state,
      action: PayloadAction<{
        context: keyof ColumnResizeState;
        widths: WidthMap;
      }>,
    ) => {
      state[action.payload.context] = action.payload.widths;
    },

    /**
     * Clear all persisted column widths for both pages.
     * Called on logout to keep the in-memory store in sync with the cleared
     * localStorage (which is wiped by `localStorage.clear()` in the logout flow).
     */
    clearColumnWidths: () => initialState,
  },
});

export const { setColumnWidth, setAllColumnWidths, clearColumnWidths } =
  columnResizeSlice.actions;

export default columnResizeSlice.reducer;
